import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { BASE_URL } from "../../../config";
import "./EngineerLeave.css";

/**
 * ─────────────────────────────────────────────────────────────────────────
 * ASSUMPTIONS (adjust to match your actual backend/auth shape):
 *
 * 1. Same localStorage/role shape as EngineerAttendance.jsx:
 *      { token, id, name, role: [{ roleName, roleDescription }], ... }
 *
 * 2. Endpoint paths use `${BASE_URL}/...` with NO "/api" prefix, matching
 *    the fix already applied in EngineerAttendance.jsx for your BASE_URL.
 *
 * 3. `leaveType` enum values are ASSUMED as SICK_LEAVE, CASUAL_LEAVE,
 *    PAID_LEAVE, UNPAID_LEAVE, EMERGENCY_LEAVE — only "SICK_LEAVE" was
 *    confirmed in your example. Update the `LEAVE_TYPES` array below with
 *    your backend's real enum values, or the dropdown will send a value
 *    the server rejects.
 *
 * 4. Confirmed: `/all/leave` returns a page grouped PER ENGINEER, not a flat
 *    list of leave requests:
 *      { content: [ { engineerId, engineerName, phoneNo, monthlyQuota,
 *                     leavesUsed, leavesRemaining, extraLeavesTaken,
 *                     salaryPerDay, totalDeduction, balanceMessage,
 *                     leaveRequests: [ { leaveRequestId, leaveType,
 *                       fromDate, toDate, totalDays, reason, leaveStatus,
 *                       reviewRemark, reviewedAt, reviewedByName,
 *                       reviewerRole, createdAt } ] } ],
 *        currentPage, totalPages, totalElements, pageSize,
 *        hasNext, hasPrevious, first, last }
 *    `AllLeaveRequests` flattens this into one table row per individual
 *    leave request (see `rows` below), showing each engineer's
 *    `balanceMessage` alongside their requests. `MyLeaveHistory` handles
 *    either this grouped shape or a flat one defensively, since its
 *    endpoint's exact shape wasn't separately confirmed.
 *
 *    NOTE: the "Engineer" filter in `AllLeaveRequests` is now a NAME
 *    search that filters the already-fetched page CLIENT-SIDE only — it
 *    never calls the API and is not part of the `filters` state that
 *    drives `fetchData`. Only status/month/year trigger a re-fetch.
 *
 * 5. Confirmed: `/engineer/{id}/leave/balance/current-month` returns:
 *      { engineerId, engineerName, month, year, monthlyQuota, leavesUsed,
 *        leavesRemaining, extraLeavesTaken, salaryPerDay, totalDeduction,
 *        effectiveSalary, approvedLeaves: [ { leaveRequestId, leaveType,
 *          fromDate, toDate, totalDays, reason, leaveStatus,
 *          reviewedByName, reviewerRole, reviewRemark, reviewedAt,
 *          createdAt } ], message }
 *    `LeaveBalanceCard` renders this as fixed fields (quota/used/remaining/
 *    extra pills, a usage bar, a salary strip, and an approved-leaves
 *    table) rather than the generic key/value fallback used before.
 *
 * 6. `reviewerId` is the logged-in admin/superengineer's own `id`, and
 *    `reviewerRole` is their role name pulled from the same `role` array
 *    (uppercased). If your backend expects a specific casing/string for
 *    reviewerRole (e.g. "Admin" vs "ADMIN"), adjust `reviewerRole` in
 *    ReviewLeaveModal.
 * ─────────────────────────────────────────────────────────────────────────
 */

// ─── Helpers ─────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ASSUMPTION — confirm real enum values with your backend (see note #3 above)
const LEAVE_TYPES = ["SICK_LEAVE", "CASUAL_LEAVE", "PAID_LEAVE", "UNPAID_LEAVE", "EMERGENCY_LEAVE"];
const LEAVE_STATUSES = ["PENDING", "APPROVED", "REJECTED", "CANCELLED"];

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("NagpurProperties")) || {};
  } catch {
    return {};
  }
}

function getRoleNames(user) {
  const roles = Array.isArray(user?.role) ? user.role : [];
  return roles.map((r) => (r?.roleName || "").trim().toUpperCase());
}

// Confirmed page shape uses hasNext/hasPrevious/currentPage/pageSize; fall
// back to the standard Spring Data first/last/number/size names in case a
// different endpoint on this same backend uses that shape instead.
function pageFlags(data, page, itemsLen, size) {
  if (!data) return { isFirst: page === 0, isLast: itemsLen < size };
  const isFirst = data.hasPrevious !== undefined ? !data.hasPrevious : data.first ?? page === 0;
  const isLast = data.hasNext !== undefined ? !data.hasNext : data.last ?? itemsLen < size;
  return { isFirst, isLast };
}

function formatLabel(text) {
  if (!text) return "—";
  return text
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  const parts = [];
  if (data.message) {
    if (Array.isArray(data.message)) parts.push(...data.message);
    else parts.push(data.message);
  }
  if (Array.isArray(data.errors)) {
    data.errors.forEach((e) => parts.push(typeof e === "string" ? e : e.defaultMessage || JSON.stringify(e)));
  } else if (data.errors && typeof data.errors === "object") {
    Object.entries(data.errors).forEach(([field, msg]) => parts.push(`${field}: ${msg}`));
  }
  return parts.length ? parts.join("\n") : `Request failed (${error?.response?.status || "unknown"})`;
}

// ─── Badge ───────────────────────────────────────────────────────────────
function LeaveStatusBadge({ status }) {
  const map = {
    PENDING: "lv-badge-pending",
    APPROVED: "lv-badge-approved",
    REJECTED: "lv-badge-rejected",
    CANCELLED: "lv-badge-cancelled",
  };
  return <span className={`lv-badge ${map[status] || "lv-badge-default"}`}>{formatLabel(status)}</span>;
}

// ─── Self: leave balance ─────────────────────────────────────────────────
function LeaveBalanceCard({ engineerId }) {
  const { token } = getStoredUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    if (!engineerId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/engineer/${engineerId}/leave/balance/current-month`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      console.error("Leave balance fetch failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [engineerId, token]);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const approvedLeaves = data?.approvedLeaves || [];
  const usagePct = data?.monthlyQuota
    ? Math.min(100, Math.round((data.leavesUsed / data.monthlyQuota) * 100))
    : 0;
  const fmtMoney = (n) => (typeof n === "number" ? `₹ ${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—");

  return (
    <div className="lv-card">
      <div className="lv-card-header">
        <span className="lv-card-title">Leave Balance</span>
        {data && <span className="lv-month-chip">{formatLabel(data.month)} {data.year}</span>}
      </div>

      {loading ? (
        <div className="lv-empty">Loading balance…</div>
      ) : !data ? (
        <div className="lv-empty">No balance data found.</div>
      ) : (
        <div className="lv-balance-body">
          {data.message && <div className="lv-balance-message">{data.message}</div>}

          <div className="lv-usage-bar-wrap">
            <div className="lv-usage-bar-track">
              <div className="lv-usage-bar-fill" style={{ width: `${usagePct}%` }} />
            </div>
            <div className="lv-usage-bar-label">{data.leavesUsed} of {data.monthlyQuota} used this month</div>
          </div>

          <div className="lv-balance-grid">
            <div className="lv-balance-pill">
              <span>{data.monthlyQuota}</span>
              Monthly Quota
            </div>
            <div className="lv-balance-pill lv-pill-amber">
              <span>{data.leavesUsed}</span>
              Used
            </div>
            <div className="lv-balance-pill lv-pill-green">
              <span>{data.leavesRemaining}</span>
              Remaining
            </div>
            <div className={`lv-balance-pill ${data.extraLeavesTaken > 0 ? "lv-pill-red" : ""}`}>
              <span>{data.extraLeavesTaken}</span>
              Extra Taken
            </div>
          </div>

          <div className="lv-salary-strip">
            <div className="lv-salary-item">
              <span className="lv-salary-label">Salary / Day</span>
              <span className="lv-salary-value">{fmtMoney(data.salaryPerDay)}</span>
            </div>
            <div className="lv-salary-item">
              <span className="lv-salary-label">Deduction</span>
              <span className={`lv-salary-value ${data.totalDeduction > 0 ? "lv-text-red" : ""}`}>
                {fmtMoney(data.totalDeduction)}
              </span>
            </div>
            <div className="lv-salary-item">
              <span className="lv-salary-label">Effective Salary</span>
              <span className="lv-salary-value lv-text-green">{fmtMoney(data.effectiveSalary)}</span>
            </div>
          </div>

          {approvedLeaves.length > 0 && (
            <div className="lv-approved-section">
              <div className="lv-approved-title">Approved Leaves This Month</div>
              <table className="lv-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Days</th>
                    <th>Reviewed By</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedLeaves.map((l) => (
                    <tr key={l.leaveRequestId}>
                      <td>{formatLabel(l.leaveType)}</td>
                      <td>{l.fromDate}</td>
                      <td>{l.toDate}</td>
                      <td>{l.totalDays}</td>
                      <td className="lv-muted">{l.reviewedByName || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Self: apply for leave ───────────────────────────────────────────────
function ApplyLeaveForm({ engineerId, onApplied }) {
  const { token } = getStoredUser();
  const [leaveType, setLeaveType] = useState(LEAVE_TYPES[0]);
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(todayISO());
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    !!leaveType && !!fromDate && !!toDate && toDate >= fromDate && reason.trim() !== "" && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(
        `${BASE_URL}/engineer/${engineerId}/leave`,
        { leaveType, fromDate, toDate, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Leave request submitted.");
      setReason("");
      onApplied?.();
    } catch (error) {
      console.error("Apply leave failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="lv-card">
      <div className="lv-card-header">
        <span className="lv-card-title">Apply for Leave</span>
      </div>
      <form className="lv-form" onSubmit={handleSubmit}>
        <div className="lv-form-row">
          <div className="lv-form-group">
            <label>Leave type *</label>
            <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
              {LEAVE_TYPES.map((t) => (
                <option key={t} value={t}>{formatLabel(t)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="lv-form-row">
          <div className="lv-form-group">
            <label>From *</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="lv-form-group">
            <label>To *</label>
            <input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
        <div className="lv-form-group">
          <label>Reason *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Briefly describe the reason for leave"
          />
        </div>
        <button type="submit" className="lv-btn-primary" disabled={!canSubmit}>
          {submitting ? "Submitting…" : "Submit Request"}
        </button>
      </form>
    </div>
  );
}

// ─── Self: leave history (paginated) ─────────────────────────────────────
function MyLeaveHistory({ engineerId, refreshKey }) {
  const { token } = getStoredUser();
  const [page, setPage] = useState(0);
  const size = 10;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!engineerId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/engineer/${engineerId}/leave`, {
        params: { page, size },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      console.error("Leave history fetch failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [engineerId, page, token]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const pageData = data?.content || [];
  // Defensive: if this endpoint shares the admin one's grouped-by-engineer
  // shape (content items with a nested `leaveRequests` array), flatten it.
  // Otherwise assume content items are already individual leave requests.
  const items = pageData.length && Array.isArray(pageData[0]?.leaveRequests)
    ? pageData.flatMap((g) => (g.leaveRequests || []).map((lr) => ({ ...lr, engineerId: g.engineerId })))
    : pageData;
  const { isFirst, isLast } = pageFlags(data, page, items.length, size);

  return (
    <div className="lv-card">
      <div className="lv-card-header">
        <span className="lv-card-title">My Leave History</span>
      </div>
      {loading ? (
        <div className="lv-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="lv-empty">No leave requests yet.</div>
      ) : (
        <>
          <table className="lv-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.leaveRequestId || l.id}>
                  <td>{formatLabel(l.leaveType)}</td>
                  <td>{l.fromDate}</td>
                  <td>{l.toDate}</td>
                  <td className="lv-muted">{l.reason || "—"}</td>
                  <td><LeaveStatusBadge status={l.leaveStatus || l.status} /></td>
                  <td className="lv-muted">{l.reviewRemark || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="lv-pagination">
            <button className="lv-btn-ghost lv-btn-sm" disabled={isFirst} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Prev
            </button>
            <span className="lv-page-info">Page {page + 1}{data?.totalPages ? ` of ${data.totalPages}` : ""}</span>
            <button className="lv-btn-ghost lv-btn-sm" disabled={isLast} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Admin: review modal ─────────────────────────────────────────────────
function ReviewLeaveModal({ leave, onClose, onReviewed }) {
  const user = getStoredUser();
  const { token } = user;
  const roleNames = getRoleNames(user);
  const reviewerRole =
    roleNames.find((r) => ["ADMIN", "SUPERENGINEER", "SUPER_ENGINEER", "SUPER ENGINEER"].includes(r)) ||
    roleNames[0] ||
    "ADMIN";
  const reviewerId = user.id;

  const [leaveStatus, setLeaveStatus] = useState("APPROVED");
  const [reviewRemark, setReviewRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await axiosInstance.put(
        `${BASE_URL}/leave/${leave.leaveRequestId || leave.id}/review`,
        { leaveStatus, reviewRemark: reviewRemark.trim() },
        { params: { reviewerId, reviewerRole }, headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Leave request reviewed.");
      onReviewed?.();
    } catch (error) {
      console.error("Leave review failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="lv-modal-overlay" onClick={onClose}>
      <div className="lv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lv-modal-header">
          <h3 className="lv-modal-title">Review Leave Request</h3>
          <button className="lv-close-btn" onClick={onClose}>✕</button>
        </div>

        <p className="lv-modal-sub">
          {leave.engineerName || `Engineer #${leave.engineerId}`} — {formatLabel(leave.leaveType)}
          <br />
          {leave.fromDate} to {leave.toDate}
        </p>
        {leave.reason && (
          <p className="lv-modal-reason"><strong>Reason:</strong> {leave.reason}</p>
        )}

        <div className="lv-form-group">
          <label>Decision *</label>
          <div className="lv-decision-options">
            <label className={`lv-decision-option lv-decision-approve ${leaveStatus === "APPROVED" ? "selected" : ""}`}>
              <input
                type="radio"
                name="leaveDecision"
                checked={leaveStatus === "APPROVED"}
                onChange={() => setLeaveStatus("APPROVED")}
              />
              Approve
            </label>
            <label className={`lv-decision-option lv-decision-reject ${leaveStatus === "REJECTED" ? "selected" : ""}`}>
              <input
                type="radio"
                name="leaveDecision"
                checked={leaveStatus === "REJECTED"}
                onChange={() => setLeaveStatus("REJECTED")}
              />
              Reject
            </label>
          </div>
        </div>

        <div className="lv-form-group">
          <label>Remark (optional)</label>
          <textarea
            value={reviewRemark}
            onChange={(e) => setReviewRemark(e.target.value)}
            rows={3}
            placeholder="Add a note for the engineer"
          />
        </div>

        <div className="lv-modal-actions">
          <button className="lv-btn-primary" style={{ flex: 1 }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Review"}
          </button>
          <button className="lv-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Admin: all leave requests ────────────────────────────────────────────
function AllLeaveRequests() {
  const { token } = getStoredUser();
  const now = new Date();

  // Only these three drive the API call.
  const [filters, setFilters] = useState({
    status: "ALL",
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  });

  // Engineer name search — local/client-side only, never sent to the API
  // and never triggers a re-fetch.
  const [engineerNameQuery, setEngineerNameQuery] = useState("");

  const [page, setPage] = useState(0);
  const size = 10;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size, month: filters.month, year: filters.year };
      if (filters.status && filters.status !== "ALL") params.status = filters.status;
      const res = await axiosInstance.get(`${BASE_URL}/all/leave`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      console.error("Leave requests fetch failed:", error?.response?.data || error);
      alert(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filters, page, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function updateFilter(patch) {
    setPage(0);
    setFilters((f) => ({ ...f, ...patch }));
  }

  // The API groups results per engineer, each with its own balance summary
  // and a nested `leaveRequests` array — flatten that into one row per
  // individual leave request for the table below.
  const engineerGroups = data?.content || [];
  const rows = [];
  engineerGroups.forEach((group) => {
    (group.leaveRequests || []).forEach((lr) => {
      rows.push({
        ...lr,
        engineerId: group.engineerId,
        engineerName: group.engineerName,
        phoneNo: group.phoneNo,
        balanceMessage: group.balanceMessage,
      });
    });
  });

  // Client-side name filter over the current page's rows — no API call.
  const nameQuery = engineerNameQuery.trim().toLowerCase();
  const filteredRows = nameQuery
    ? rows.filter((l) => (l.engineerName || "").toLowerCase().includes(nameQuery))
    : rows;

  // Pagination reflects the server-fetched page, not the local name filter.
  const { isFirst, isLast } = pageFlags(data, page, rows.length, size);

  return (
    <div className="lv-card">
      <div className="lv-card-header">
        <span className="lv-card-title">All Leave Requests</span>
      </div>

      <div className="lv-filters">
        <input
          type="text"
          placeholder="Search by engineer name"
          value={engineerNameQuery}
          onChange={(e) => setEngineerNameQuery(e.target.value)}
        />
        <select value={filters.status} onChange={(e) => updateFilter({ status: e.target.value })}>
          <option value="ALL">All Statuses</option>
          {LEAVE_STATUSES.map((s) => (
            <option key={s} value={s}>{formatLabel(s)}</option>
          ))}
        </select>
        <select value={filters.month} onChange={(e) => updateFilter({ month: Number(e.target.value) })}>
          {monthNames.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>
        <input
          type="number"
          className="lv-year-input"
          value={filters.year}
          onChange={(e) => updateFilter({ year: Number(e.target.value) })}
        />
      </div>

      {loading ? (
        <div className="lv-empty">Loading…</div>
      ) : filteredRows.length === 0 ? (
        <div className="lv-empty">
          {rows.length === 0
            ? "No leave requests match these filters."
            : "No engineers match that name on this page."}
        </div>
      ) : (
        <>
          <table className="lv-table">
            <thead>
              <tr>
                <th>Engineer</th>
                <th>Balance</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Reviewed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((l) => (
                <tr key={`${l.engineerId}-${l.leaveRequestId}`}>
                  <td>
                    {l.engineerName || `#${l.engineerId}`}
                    {l.phoneNo && <div className="lv-muted" style={{ fontSize: 11.5 }}>{l.phoneNo}</div>}
                  </td>
                  <td className="lv-muted" style={{ fontSize: 12 }}>{l.balanceMessage || "—"}</td>
                  <td>{formatLabel(l.leaveType)}</td>
                  <td>{l.fromDate}</td>
                  <td>{l.toDate}</td>
                  <td>{l.totalDays ?? "—"}</td>
                  <td className="lv-muted">{l.reason || "—"}</td>
                  <td><LeaveStatusBadge status={l.leaveStatus} /></td>
                  <td className="lv-muted" style={{ fontSize: 12 }}>
                    {l.leaveStatus === "PENDING" ? (
                      "—"
                    ) : (
                      <>
                        {l.reviewedByName && <div>{l.reviewedByName}</div>}
                        {l.reviewRemark && <div>{l.reviewRemark}</div>}
                      </>
                    )}
                  </td>
                  <td>
                    {l.leaveStatus === "PENDING" && (
                      <button className="lv-btn-ghost lv-btn-sm" onClick={() => setReviewTarget(l)}>
                        Review
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="lv-pagination">
            <button className="lv-btn-ghost lv-btn-sm" disabled={isFirst} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Prev
            </button>
            <span className="lv-page-info">Page {page + 1}{data?.totalPages ? ` of ${data.totalPages}` : ""}</span>
            <button className="lv-btn-ghost lv-btn-sm" disabled={isLast} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </>
      )}

      {reviewTarget && (
        <ReviewLeaveModal
          leave={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onReviewed={() => { setReviewTarget(null); fetchData(); }}
        />
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────
function EngineerLeave() {
  const user = getStoredUser();
  const roleNames = getRoleNames(user);
  const isEngineer = roleNames.includes("ENGINEER");
  const isAdmin = roleNames.some((r) =>
    ["ADMIN", "SUPERENGINEER", "SUPER_ENGINEER", "SUPER ENGINEER"].includes(r)
  );
  const engineerId = user.engineerId || user.id;

  const [activeTab, setActiveTab] = useState(isEngineer ? "self" : "team");
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="lv-page">
      <div className="lv-topbar">
        <div>
          <h2 className="lv-page-title">Leave</h2>
          <p className="lv-page-sub">
            {isEngineer ? "Apply for leave and track your requests" : "Review and manage engineer leave requests"}
          </p>
        </div>
      </div>

      {isEngineer && isAdmin && (
        <div className="lv-tabs">
          <button className={`lv-tab ${activeTab === "self" ? "active" : ""}`} onClick={() => setActiveTab("self")}>
            My Leave
          </button>
          <button className={`lv-tab ${activeTab === "team" ? "active" : ""}`} onClick={() => setActiveTab("team")}>
            Team Leave
          </button>
        </div>
      )}

      {isEngineer && (!isAdmin || activeTab === "self") && (
        <>
          <LeaveBalanceCard engineerId={engineerId} />
          <ApplyLeaveForm engineerId={engineerId} onApplied={() => setRefreshKey((k) => k + 1)} />
          <MyLeaveHistory engineerId={engineerId} refreshKey={refreshKey} />
        </>
      )}

      {isAdmin && (!isEngineer || activeTab === "team") && <AllLeaveRequests />}

      {!isEngineer && !isAdmin && (
        <div className="lv-empty">Your account role doesn't have access to leave management.</div>
      )}
    </div>
  );
}

export default EngineerLeave;